import axios from 'axios'

// API URL from environment variable or default to localhost
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // Let axios auto-set Content-Type with boundary for FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('API Error:', error.config?.method?.toUpperCase(), error.config?.url, 'Status:', error.response?.status)
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Lead Types
export interface Lead {
  id: number
  name: string
  email?: string
  phone?: string
  company?: string
  position?: string
  avatar?: string
  document_type?: 'cpf' | 'cnpj' | 'none'
  cpf_cnpj?: string
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  estado?: string
  pais?: string
  compras_count?: number
  ultima_compra?: string
  ticket_medio?: number
  gasto_total?: number
  source: string
  source_display: string
  status: string
  status_display: string
  priority: string
  priority_display: string
  notes?: string
  estimated_value?: number
  probability?: number
  owner?: number
  owner_name?: string
  tags?: Tag[]
  interaction_count?: number
  first_contact_date?: string
  last_contact_date?: string
  follow_up_date?: string
  conversion_date?: string
  instagram_handle?: string
  facebook_url?: string
  linkedin_url?: string
  custom_fields?: Record<string, any>
  created_at: string
  updated_at: string
}

export interface Tag {
  id: number
  name: string
  color: string
  description?: string
  created_at: string
  updated_at: string
}

export interface LeadCreate {
  name: string
  email?: string
  phone?: string
  company?: string
  position?: string
  avatar?: string
  document_type?: 'cpf' | 'cnpj' | 'none'
  cpf_cnpj?: string
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  estado?: string
  pais?: string
  source: string
  status: string
  priority: string
  notes?: string
  estimated_value?: number
  probability?: number
  owner?: number
  first_contact_date?: string
  follow_up_date?: string
  instagram_handle?: string
  facebook_url?: string
  linkedin_url?: string
  custom_fields?: Record<string, any>
  tags?: number[]
}

export interface LeadUpdate {
  name?: string
  email?: string
  phone?: string
  company?: string
  position?: string
  avatar?: string
  document_type?: 'cpf' | 'cnpj' | 'none'
  cpf_cnpj?: string
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  estado?: string
  pais?: string
  compras_count?: number
  ultima_compra?: string
  ticket_medio?: number
  gasto_total?: number
  source?: string
  status?: string
  priority?: string
  notes?: string
  estimated_value?: number
  probability?: number
  owner?: number
  first_contact_date?: string
  last_contact_date?: string
  follow_up_date?: string
  conversion_date?: string
  instagram_handle?: string
  facebook_url?: string
  linkedin_url?: string
  custom_fields?: Record<string, any>
  tags?: number[]
}

// Lead API
export const leadsApi = {
  // List all leads
  list: async (params?: {
    status?: string
    priority?: string
    source?: string
    owner?: string | number
    search?: string
    ordering?: string
    page?: number
    page_size?: number
  }) => {
    const response = await api.get<Lead[]>('/leads/leads/', { params })
    return response.data
  },

  // Get lead details
  get: async (id: number) => {
    const response = await api.get<Lead>(`/leads/leads/${id}/`)
    return response.data
  },

  // Create new lead
  create: async (data: LeadCreate) => {
    const response = await api.post<Lead>('/leads/leads/', data)
    return response.data
  },

  // Update lead
  update: async (id: number, data: LeadUpdate) => {
    const response = await api.put<Lead>(`/leads/leads/${id}/`, data)
    return response.data
  },

  // Partial update lead
  patch: async (id: number, data: Partial<LeadUpdate>) => {
    const response = await api.patch<Lead>(`/leads/leads/${id}/`, data)
    return response.data
  },

  // Delete lead
  delete: async (id: number) => {
    await api.delete(`/leads/leads/${id}/`)
  },

  // Bulk delete preview
  bulkDeletePreview: async (ids: number[], includeDeals = false) => {
    const response = await api.post('/leads/leads/bulk-delete-preview/', { ids, include_deals: includeDeals })
    return response.data
  },

  // Bulk delete
  bulkDelete: async (ids: number[], includeDeals = false) => {
    const response = await api.post('/leads/leads/bulk-delete/', { ids, include_deals: includeDeals })
    return response.data
  },

  // List tags
  listTags: async () => {
    const response = await api.get<Tag[]>('/leads/tags/')
    return response.data
  },

  // Create tag
  createTag: async (data: { name: string; color: string; description?: string }) => {
    const response = await api.post<Tag>('/leads/tags/', data)
    return response.data
  },

  // Delete tag
  deleteTag: async (id: number) => {
    await api.delete(`/leads/tags/${id}/`)
  },
}

// Dashboard Stats
export const dashboardApi = {
  getStats: async () => {
    const response = await api.get<{
      total_leads: number
      active_deals: number
      total_value: number
      leads_by_status: Record<string, number>
      leads_by_source: Record<string, number>
    }>('/dashboard/stats/')
    return response.data
  }
}

// Opportunity Types
export interface Pipeline {
  id: number
  name: string
  description?: string
  group_name?: string
  stages_count: number
  created_at: string
  updated_at: string
}

export interface Stage {
  id: number
  pipeline: number
  pipeline_name: string
  name: string
  color: string
  order_index: number
  opportunities_count: number
  opportunities?: Opportunity[]
  created_at: string
  updated_at: string
}

export interface Product {
  id: number
  name: string
  description?: string
  sku?: string
  price: number
  quantity: number
  total_price: number
  opportunity: number
  created_at: string
  updated_at: string
}

export interface Opportunity {
  id: number
  lead: number
  lead_name: string
  lead_email?: string
  lead_company?: string
  pipeline: number
  pipeline_name: string
  stage: number
  stage_name: string
  stage_color: string
  attendant?: number
  attendant_name?: string
  internal_code: string
  external_id?: string
  total_value: number
  discount: number
  addition: number
  freight: number
  coupon_code?: string
  freight_type?: string
  status: 'em_processo' | 'ganho' | 'perdido'
  status_display: string
  loss_reason?: string
  last_moved_at: string
  last_status_change?: string
  notes?: string
  final_value: number
  total_products: number
  products?: Product[]
  created_at: string
  updated_at: string
}

export interface OpportunityCreate {
  lead: number
  pipeline: number
  stage: number
  attendant?: number
  internal_code: string
  external_id?: string
  total_value: number
  discount?: number
  addition?: number
  freight?: number
  coupon_code?: string
  freight_type?: string
  status?: 'em_processo' | 'ganho' | 'perdido'
  loss_reason?: string
  notes?: string
  products?: ProductCreate[]
}

export interface OpportunityUpdate {
  lead?: number
  pipeline?: number
  stage?: number
  attendant?: number
  internal_code?: string
  external_id?: string
  total_value?: number
  discount?: number
  addition?: number
  freight?: number
  coupon_code?: string
  freight_type?: string
  status?: 'em_processo' | 'ganho' | 'perdido'
  loss_reason?: string
  notes?: string
  products?: ProductCreate[]
}

export interface ProductCreate {
  name: string
  description?: string
  sku?: string
  price: number
  quantity: number
  opportunity: number
}

// Opportunity API
export const opportunitiesApi = {
  // List all opportunities
  list: async (params?: {
    lead?: number
    pipeline?: number
    stage?: number
    attendant?: number
    status?: string
    search?: string
    ordering?: string
  }) => {
    const response = await api.get<Opportunity[]>('/opportunities/opportunities/', { params })
    return response.data
  },

  // Get opportunity details
  get: async (id: number) => {
    const response = await api.get<Opportunity>(`/opportunities/opportunities/${id}/`)
    return response.data
  },

  // Create new opportunity
  create: async (data: OpportunityCreate) => {
    const response = await api.post<Opportunity>('/opportunities/opportunities/', data)
    return response.data
  },

  // Update opportunity
  update: async (id: number, data: OpportunityUpdate) => {
    const response = await api.put<Opportunity>(`/opportunities/opportunities/${id}/`, data)
    return response.data
  },

  // Partial update opportunity
  patch: async (id: number, data: Partial<OpportunityUpdate>) => {
    const response = await api.patch<Opportunity>(`/opportunities/opportunities/${id}/`, data)
    return response.data
  },

  // Delete opportunity
  delete: async (id: number) => {
    await api.delete(`/opportunities/opportunities/${id}/`)
  },

  // Move opportunity to stage
  moveToStage: async (id: number, stageId: number) => {
    const response = await api.post<Opportunity>(`/opportunities/opportunities/${id}/move_to_stage/`, { stage_id: stageId })
    return response.data
  },

  // Update opportunity status
  updateStatus: async (id: number, status: 'em_processo' | 'ganho' | 'perdido', lossReason?: string) => {
    const response = await api.post<Opportunity>(`/opportunities/opportunities/${id}/update_status/`, { 
      status,
      loss_reason: lossReason
    })
    return response.data
  },

  // Get opportunities by pipeline
  getByPipeline: async (pipelineId: number) => {
    const response = await api.get<Stage[]>(`/opportunities/opportunities/by_pipeline/`, { params: { pipeline_id: pipelineId } })
    return response.data
  },

  // Get dashboard stats
  getDashboardStats: async () => {
    const response = await api.get<{
      total: number
      total_value: number
      total_final_value: number
      by_status: {
        em_processo: number
        ganho: number
        perdido: number
      }
      recent_wins: Opportunity[]
      recent_losses: Opportunity[]
    }>('/opportunities/opportunities/dashboard_stats/')
    return response.data
  },

  // Get opportunity products
  getProducts: async (id: number) => {
    const response = await api.get<Product[]>(`/opportunities/opportunities/${id}/products/`)
    return response.data
  },
}

// Pipeline API
export const pipelinesApi = {
  // List all pipelines
  list: async () => {
    const response = await api.get<Pipeline[]>('/pipelines/')
    return response.data
  },

  // Get pipeline details
  get: async (id: number) => {
    const response = await api.get<Pipeline>(`/pipelines/${id}/`)
    return response.data
  },

  // Create new pipeline
  create: async (data: Partial<Pipeline>) => {
    const response = await api.post<Pipeline>('/pipelines/', data)
    return response.data
  },

  // Update pipeline
  update: async (id: number, data: Partial<Pipeline>) => {
    const response = await api.put<Pipeline>(`/pipelines/${id}/`, data)
    return response.data
  },

  // Delete pipeline
  delete: async (id: number) => {
    await api.delete(`/pipelines/${id}/`)
  },

  // Get pipeline with stages and opportunities
  getFull: async (id: number) => {
    const response = await api.get<Pipeline & { stages: Stage[] }>(`/pipelines/${id}/full/`)
    return response.data
  },

  // Get pipeline stages
  getStages: async (id: number) => {
    const response = await api.get<Stage[]>(`/pipelines/${id}/stages/`)
    return response.data
  },

  // Get pipeline opportunities
  getOpportunities: async (id: number) => {
    const response = await api.get<Opportunity[]>(`/pipelines/${id}/opportunities/`)
    return response.data
  },

  // Get pipeline stats
  getStats: async (id: number) => {
    const response = await api.get<{
      total_opportunities: number
      total_value: number
      by_status: any[]
      by_stage: Array<{
        id: number
        name: string
        color: string
        count: number
      }>
    }>(`/pipelines/${id}/stats/`)
    return response.data
  },
}

// Stage API
export const stagesApi = {
  // List all stages
  list: async (params?: { pipeline?: number }) => {
    const response = await api.get<Stage[]>('/opportunities/stages/', { params })
    return response.data
  },

  // Get stage details
  get: async (id: number) => {
    const response = await api.get<Stage>(`/opportunities/stages/${id}/`)
    return response.data
  },

  // Create new stage
  create: async (data: Partial<Stage>) => {
    const response = await api.post<Stage>('/opportunities/stages/', data)
    return response.data
  },

  // Update stage
  update: async (id: number, data: Partial<Stage>) => {
    const response = await api.put<Stage>(`/opportunities/stages/${id}/`, data)
    return response.data
  },

  // Delete stage
  delete: async (id: number) => {
    await api.delete(`/opportunities/stages/${id}/`)
  },

  // Get stage with opportunities
  getFull: async (id: number) => {
    const response = await api.get<Stage & { opportunities: Opportunity[] }>(`/opportunities/stages/${id}/full/`)
    return response.data
  },

  // Get stage opportunities
  getOpportunities: async (id: number) => {
    const response = await api.get<Opportunity[]>(`/opportunities/stages/${id}/opportunities/`)
    return response.data
  },
}

// Product API
export const productsApi = {
  // List all products
  list: async (params?: { opportunity?: number }) => {
    const response = await api.get<Product[]>('/opportunities/products/', { params })
    return response.data
  },

  // Get product details
  get: async (id: number) => {
    const response = await api.get<Product>(`/opportunities/products/${id}/`)
    return response.data
  },

  // Create new product
  create: async (data: ProductCreate) => {
    const response = await api.post<Product>('/opportunities/products/', data)
    return response.data
  },

  // Update product
  update: async (id: number, data: Partial<ProductCreate>) => {
    const response = await api.put<Product>(`/opportunities/products/${id}/`, data)
    return response.data
  },

  // Delete product
  delete: async (id: number) => {
    await api.delete(`/opportunities/products/${id}/`)
  },
}

// Activity Types
export interface Activity {
  id: number
  title: string
  description?: string
  activity_type: 'call' | 'email' | 'meeting' | 'task' | 'note' | 'followup' | 'other' | 'lunch' | 'visit' | 'whatsapp'
  activity_type_display: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  priority_display: string
  status: 'scheduled' | 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'missed'
  status_display: string
  is_overdue: boolean
  scheduled_at: string | null
  started_at?: string | null
  completed_at?: string | null
  follow_up_date?: string | null
  duration_minutes?: number
  lead?: number | { id: number; name: string }
  lead_name?: string
  deal?: number | { id: number; name: string }
  deal_name?: string
  assigned_to?: number | { id: number; get_full_name?: string }
  assigned_to_name?: string
  created_by?: number | { id: number; get_full_name?: string }
  created_by_name?: string
  color?: string
  notes?: string
  location?: string
  outcome?: string
  is_private?: boolean
  created_at: string
  updated_at: string
}

export interface ActivityCreate {
  title: string
  description?: string
  activity_type: 'call' | 'email' | 'meeting' | 'task' | 'note' | 'followup' | 'other' | 'lunch' | 'visit' | 'whatsapp'
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  status?: 'scheduled' | 'pending' | 'in_progress' | 'completed' | 'cancelled'
  scheduled_at?: string | null
  follow_up_date?: string | null
  lead?: number | null
  deal?: number | null
  assigned_to?: number | null
  notes?: string
  location?: string
}

export interface ActivityUpdate {
  title?: string
  description?: string
  activity_type?: 'call' | 'email' | 'meeting' | 'task' | 'note' | 'followup' | 'other' | 'lunch' | 'visit' | 'whatsapp'
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  status?: 'scheduled' | 'pending' | 'in_progress' | 'completed' | 'cancelled'
  scheduled_at?: string | null
  follow_up_date?: string | null
  lead?: number | null
  deal?: number | null
  assigned_to?: number | null
  notes?: string
  location?: string
  outcome?: string
}

// Activity API
export const activitiesApi = {
  // List all activities
  list: async (params?: {
    activity_type?: string
    status?: string
    priority?: string
    lead?: number
    assigned_to?: number
    search?: string
    ordering?: string
  }) => {
    const response = await api.get<Activity[]>('/activities/activities/', { params })
    return response.data
  },

  // Get activity details
  get: async (id: number) => {
    const response = await api.get<Activity>(`/activities/activities/${id}/`)
    return response.data
  },

  // Create new activity
  create: async (data: ActivityCreate) => {
    const response = await api.post<Activity>('/activities/activities/', data)
    return response.data
  },

  // Update activity
  update: async (id: number, data: ActivityUpdate) => {
    const response = await api.put<Activity>(`/activities/activities/${id}/`, data)
    return response.data
  },

  // Partial update activity
  patch: async (id: number, data: Partial<ActivityUpdate>) => {
    const response = await api.patch<Activity>(`/activities/activities/${id}/`, data)
    return response.data
  },

  // Delete activity
  delete: async (id: number) => {
    await api.delete(`/activities/activities/${id}/`)
  },

  // Start an activity
  start: async (id: number) => {
    const response = await api.post<Activity>(`/activities/activities/${id}/start/`)
    return response.data
  },

  // Complete an activity
  complete: async (id: number) => {
    const response = await api.post<Activity>(`/activities/activities/${id}/complete/`)
    return response.data
  },

  // Cancel an activity
  cancel: async (id: number) => {
    const response = await api.post<Activity>(`/activities/activities/${id}/cancel/`)
    return response.data
  },

  // Get statistics
  statistics: async () => {
    const response = await api.get<{
      total: number
      by_status: Record<string, { label: string; count: number }>
      by_priority: Record<string, { label: string; count: number }>
      by_type: Record<string, { label: string; count: number }>
      overdue: number
      today: number
      this_week: number
    }>('/activities/activities/statistics/')
    return response.data
  },
}

// Deals Bulk API
export const dealsApi = {
  bulkDeletePreview: async (ids: number[]) => {
    const response = await api.post('/deals/deals/bulk-delete-preview/', { ids })
    return response.data
  },
  bulkDelete: async (ids: number[]) => {
    const response = await api.post('/deals/deals/bulk-delete/', { ids })
    return response.data
  },
}

// Contacts Bulk API
export const contactsApi = {
  bulkDeletePreview: async (ids: number[]) => {
    const response = await api.post('/messaging/contacts/bulk-delete-preview/', { ids })
    return response.data
  },
  bulkDelete: async (ids: number[]) => {
    const response = await api.post('/messaging/contacts/bulk-delete/', { ids })
    return response.data
  },
}

// Messaging API
export interface UnreadCount {
  unread_count: number
  conversations_with_unread: number
}

export const messagingApi = {
  // Get unread messages count
  getUnreadCount: async () => {
    const response = await api.get<UnreadCount>('/messaging/unread-count/')
    return response.data
  },
}

// Import billing types
import type {
  SubscriptionPlan,
  Subscription,
  Invoice,
} from '../types/billing'

// Type for DRF paginated responses
export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// Type guard to check if response is paginated
export function isPaginatedResponse<T>(response: any): response is PaginatedResponse<T> {
  return response && typeof response === 'object' && 'results' in response
}

// Billing API
export const billingApi = {
  // Get current subscription and usage
  getCurrent: async () => {
    const response = await api.get<Subscription>('/billing/subscription/')
    return response.data
  },

  // Get current usage metrics
  getUsage: async () => {
    const response = await api.get<{
      pipelines_count: number
      pipelines_limit: number
      leads_count: number
      leads_limit: number
      members_count: number
      members_limit: number
      automations_count: number
      automations_limit: number
      connections_count: number
      connections_limit: number
      webhooks_count: number
      webhooks_limit: number
    }>('/billing/subscription/usage/')
    return response.data
  },

  // List all available plans
  listPlans: async () => {
    const response = await api.get<SubscriptionPlan[] | { count: number; next: string | null; previous: string | null; results: SubscriptionPlan[] }>('/billing/plans/')
    return response.data
  },

  // Get plan details
  getPlan: async (id: string) => {
    const response = await api.get<SubscriptionPlan>(`/billing/plans/${id}/`)
    return response.data
  },

  // Subscribe to a plan (create checkout session)
  subscribe: async (planId: string | number, billingCycle: 'monthly' | 'semestral' | 'anual' = 'monthly') => {
    const response = await api.post<{checkout_url: string; session_id: string}>('/billing/subscription/create-checkout-session/', {
      plan_id: planId,
      billing_cycle: billingCycle
    })
    return response.data
  },

  // Cancel subscription
  cancelSubscription: async () => {
    const response = await api.post<Subscription>('/billing/subscription/cancel/')
    return response.data
  },

  // Reactivate subscription
  reactivateSubscription: async () => {
    const response = await api.post<Subscription>('/billing/subscription/reactivate/')
    return response.data
  },

  // Update subscription plan (upgrade/downgrade)
  updatePlan: async (planId: string | number, billingCycle: 'monthly' | 'semestral' | 'anual' = 'monthly') => {
    const response = await api.post<Subscription>('/billing/subscription/upgrade/', {
      plan_id: planId,
      billing_cycle: billingCycle
    })
    return response.data
  },

  // List invoices
  listInvoices: async () => {
    const response = await api.get<Invoice[]>('/billing/invoices/')
    return response.data
  },

  // Get invoice details
  getInvoice: async (id: string) => {
    const response = await api.get<Invoice>(`/billing/invoices/${id}/`)
    return response.data
  },
}

export default api
